import { type JSX, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

import { createCheckoutSession, openBillingPortal, type Plan } from '../../api.js';

interface TierCard {
  name: string;
  tier: 'pro' | 'enterprise';
  tagline: string;
  features: string[];
  monthly: { plan: Plan; price: string };
  yearly: { plan: Plan; price: string; savings: string };
}

const TIERS: TierCard[] = [
  {
    name: 'Pro',
    tier: 'pro',
    tagline: 'Everything in Core, plus the features fleets actually ask for.',
    features: ['Theming', 'Multi-profile', 'Signage mode', 'Diagnostic overlay', 'Broadcasts', 'Agent actions', 'Offline ZIP bundle'],
    monthly: { plan: 'pro_monthly', price: '$49/mo' },
    yearly: { plan: 'pro_yearly', price: '$499/yr', savings: 'save ~15%' },
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    tagline: 'For larger fleets, ruggedized hardware, and compliance shops.',
    features: ['Everything in Pro', 'Remote config', 'Telemetry export', 'SSO display', 'Audit log', 'Priority support'],
    monthly: { plan: 'ent_monthly', price: '$199/mo' },
    yearly: { plan: 'ent_yearly', price: '$1,999/yr', savings: 'save ~16%' },
  },
];

const RANK: Record<'core' | 'pro' | 'enterprise', number> = { core: 0, pro: 1, enterprise: 2 };

/** Pricing cards + checkout, or a manage-subscription button for paid tenants. */
export function SubscriptionSection({
  tenantId,
  editToken,
  currentTier,
}: {
  tenantId: string;
  editToken: string;
  currentTier: 'core' | 'pro' | 'enterprise';
}): JSX.Element {
  const [busy, setBusy] = useState<Plan | 'portal' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Return to the Subscription section of this fleet after Stripe.
  const ret = (flag: 'upgraded' | 'canceled'): string =>
    `${window.location.origin}${window.location.pathname}?${flag}=1&tenant=${encodeURIComponent(tenantId)}#fleet/${encodeURIComponent(tenantId)}/subscription`;

  async function checkout(plan: Plan): Promise<void> {
    setBusy(plan);
    setError(null);
    const res = await createCheckoutSession({ tenantId, editToken, plan, successUrl: ret('upgraded'), cancelUrl: ret('canceled') });
    if (res.kind === 'ok') {
      window.location.assign(res.url);
      return;
    }
    setBusy(null);
    setError(res.kind === 'unauthorized' ? 'Edit token rejected.' : res.kind === 'invalid' || res.kind === 'stripe' || res.kind === 'network' ? res.message : `Server returned ${String(res.status)}.`);
  }

  async function manage(): Promise<void> {
    setBusy('portal');
    setError(null);
    const res = await openBillingPortal({ tenantId, editToken, returnUrl: ret('upgraded') });
    if (res.kind === 'ok') {
      window.location.assign(res.url);
      return;
    }
    setBusy(null);
    setError(res.kind === 'no-subscription' ? 'No active subscription to manage yet.' : res.kind === 'unauthorized' ? 'Edit token rejected.' : res.kind === 'stripe' || res.kind === 'network' ? res.message : `Server returned ${String(res.status)}.`);
  }

  const visible = TIERS.filter((t) => RANK[t.tier] > RANK[currentTier]);

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>Subscription</Typography>
        <Chip size="small" label={currentTier} color={currentTier === 'core' ? 'default' : 'secondary'} />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {currentTier === 'core'
          ? 'Upgrade to unlock theming, broadcasts, remote config, telemetry, and more.'
          : 'Manage your plan, payment method, and invoices.'}
      </Typography>

      {error !== null && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {currentTier !== 'core' && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">You're on {currentTier === 'pro' ? 'Pro' : 'Enterprise'}.</Typography>
            <Typography color="text.secondary" variant="body2">Change plan, update your card, or download invoices in the billing portal.</Typography>
          </CardContent>
          <CardActions>
            <Button variant="contained" onClick={() => void manage()} disabled={busy !== null} data-testid="manage-billing">
              {busy === 'portal' ? 'Opening…' : 'Manage subscription'}
            </Button>
          </CardActions>
        </Card>
      )}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: visible.length > 1 ? '1fr 1fr' : '1fr' } }}>
        {visible.map((t) => (
          <Card key={t.tier} variant="outlined" data-testid={`tier-card-${t.tier}`}>
            <CardContent>
              <Typography variant="h6" fontWeight={700}>{t.name}</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>{t.tagline}</Typography>
              <List dense disablePadding>
                {t.features.map((f) => (
                  <ListItem key={f} disableGutters sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 28, color: 'secondary.main' }}><CheckIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={f} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={() => void checkout(t.monthly.plan)} disabled={busy !== null} data-testid={`checkout-${t.monthly.plan}`}>
                {busy === t.monthly.plan ? 'Redirecting…' : `${t.monthly.price}`}
              </Button>
              <Button variant="outlined" onClick={() => void checkout(t.yearly.plan)} disabled={busy !== null} data-testid={`checkout-${t.yearly.plan}`}>
                {busy === t.yearly.plan ? 'Redirecting…' : `${t.yearly.price} · ${t.yearly.savings}`}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
